import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Grid, TextField, Button, Box, Paper, Typography, Checkbox, FormControlLabel} from '@mui/material';
import {Autocomplete} from '@mui/material';
// import {DateRangePicker, LocalizationProvider} from '@mui/lab';
// import AdapterDateFns from '@mui/lab/AdapterDateFns'; // Make sure you're using DateFns

const FilterCardSendData = ({
  domains,
  customers,
  handleCustomerChange,
  filters,
  setFilters,
  onApply,
  handleDateRangeChange,
  handleSentDateRangeChange,
  handleDomainChange,
  handleSearchChange,
  onClear,
  disableDataSource,
  includeHighlighted,
  handleIncludeHighlightedChange,
}) => {
  // Effect to handle disabling Data Source
  useEffect(() => {
    if (disableDataSource) {
      setFilters((prev) => ({
        ...prev,
        dataSource: '',
      }));
    }
  }, [disableDataSource]);

  return (
    <Paper elevation={3} sx={{padding: 2, marginBottom: 2}}>
      <Grid container spacing={2} alignItems='center'>
        {/* Search Text Field Section */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Search
          </Typography>
          <TextField
            fullWidth
            label='Search by ID, Name, Email, Phone, Marks, MPC_4, or Other Data content'
            value={filters.search || ''}
            onChange={handleSearchChange}
            placeholder='Search by ID, name, email, phone, marks, MPC_4, or other data content'
          />
        </Grid>

        {/* Re-entered already-sent leads highlight toggle — only when wired up by the host page */}
        {handleIncludeHighlightedChange && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!includeHighlighted}
                  onChange={(e) =>
                    handleIncludeHighlightedChange(e.target.checked)
                  }
                />
              }
              label='Highlight already-sent leads re-entered in the CRM'
            />
          </Grid>
        )}

        {/* Date Range Section */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Created Date Range
          </Typography>
          <Box sx={{display: 'flex', gap: 2}}>
            <TextField
              label='From Date'
              type='date'
              value={
                filters.dateRange?.[0]
                  ? filters.dateRange[0] instanceof Date
                    ? filters.dateRange[0].toISOString().split('T')[0]
                    : typeof filters.dateRange[0] === 'string' && filters.dateRange[0].includes('T')
                    ? filters.dateRange[0].split('T')[0]
                    : filters.dateRange[0]
                  : ''
              }
              onChange={(e) =>
                handleDateRangeChange([
                  e.target.value,
                  filters.dateRange?.[1] || null,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label='To Date'
              type='date'
              value={
                filters.dateRange?.[1]
                  ? filters.dateRange[1] instanceof Date
                    ? filters.dateRange[1].toISOString().split('T')[0]
                    : typeof filters.dateRange[1] === 'string' && filters.dateRange[1].includes('T')
                    ? filters.dateRange[1].split('T')[0]
                    : filters.dateRange[1]
                  : ''
              }
              onChange={(e) =>
                handleDateRangeChange([
                  filters.dateRange?.[0] || null,
                  e.target.value,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
          </Box>
        </Grid>

        {/* Sent From-To (customer_data.sent_at) */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Sent From-To (Delivery)
          </Typography>
          <Box sx={{display: 'flex', gap: 2}}>
            <TextField
              label='Sent From Date'
              type='date'
              value={
                filters.sentDateRange?.[0]
                  ? filters.sentDateRange[0] instanceof Date
                    ? filters.sentDateRange[0].toISOString().split('T')[0]
                    : typeof filters.sentDateRange[0] === 'string' && filters.sentDateRange[0].includes('T')
                    ? filters.sentDateRange[0].split('T')[0]
                    : filters.sentDateRange[0]
                  : ''
              }
              onChange={(e) =>
                handleSentDateRangeChange([
                  e.target.value,
                  filters.sentDateRange?.[1] || null,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label='Sent To Date'
              type='date'
              value={
                filters.sentDateRange?.[1]
                  ? filters.sentDateRange[1] instanceof Date
                    ? filters.sentDateRange[1].toISOString().split('T')[0]
                    : typeof filters.sentDateRange[1] === 'string' && filters.sentDateRange[1].includes('T')
                    ? filters.sentDateRange[1].split('T')[0]
                    : filters.sentDateRange[1]
                  : ''
              }
              onChange={(e) =>
                handleSentDateRangeChange([
                  filters.sentDateRange?.[0] || null,
                  e.target.value,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
          </Box>
        </Grid>

        {/* Domain Dropdown Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Domain
          </Typography>
          <Autocomplete
            disablePortal
            options={domains}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.key === value?.key}
            value={domains.find((d) => String(d.key) === String(filters.domain)) || null}
            onChange={handleDomainChange}
            renderInput={(params) => (
              <TextField {...params} label='Domain' fullWidth />
            )}
          />
        </Grid>

        {/* Customer Dropdown Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Customer
          </Typography>
          <Autocomplete
            disablePortal
            options={customers}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.key === value?.key}
            value={customers.find((c) => String(c.key) === String(filters.customer)) || null}
            onChange={handleCustomerChange} // Use handleCustomerChange here
            renderInput={(params) => (
              <TextField {...params} label='Customer' fullWidth />
            )}
          />
        </Grid>

        {/* Country TextField Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Country
          </Typography>
          <TextField
            fullWidth
            label='Country'
            value={filters.country}
            onChange={(e) => setFilters({...filters, country: e.target.value})}
            placeholder='Enter country name'
          />
        </Grid>

        {/* Data Source Text Field Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Data Source
          </Typography>
              <TextField
            fullWidth
                label='Data Source'
            value={filters.dataSource || ''}
            onChange={(e) => setFilters({...filters, dataSource: e.target.value})}
            placeholder='Enter data source'
                disabled={disableDataSource}
          />
        </Grid>

        {/* Integration Source Text Field Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Integration Source
          </Typography>
          <TextField
            fullWidth
            label='Integration Source'
            value={filters.integrationSource || ''}
            onChange={(e) => setFilters({...filters, integrationSource: e.target.value})}
            placeholder='Enter integration source'
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box display='flex' justifyContent='flex-end' gap={2}>
            <Button variant='contained' color='primary' onClick={onApply}>
              Apply Filters
            </Button>
            <Button variant='outlined' color='secondary' onClick={onClear}>
              Clear Filters
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Updated PropTypes to reflect changes
FilterCardSendData.propTypes = {
  domains: PropTypes.array.isRequired, // New prop for domains
  customers: PropTypes.array.isRequired, // New prop for customers
  filters: PropTypes.shape({
    country: PropTypes.string,
    dataSource: PropTypes.string,
    domain: PropTypes.string, // New field for domain
    customer: PropTypes.string, // New field for customer
    search: PropTypes.string, // Search field
    dateRange: PropTypes.array, // Accept either Date or string (HTML date input)
    sentDateRange: PropTypes.array, // New: Sent from/to range
    integrationSource: PropTypes.string, // New field for integration source
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  handleDomainChange: PropTypes.func.isRequired,
  handleSearchChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  handleCustomerChange: PropTypes.func.isRequired,
  disableDataSource: PropTypes.bool,
  handleDateRangeChange: PropTypes.func.isRequired,
  handleSentDateRangeChange: PropTypes.func.isRequired,
  includeHighlighted: PropTypes.bool,
  handleIncludeHighlightedChange: PropTypes.func,
};

export default FilterCardSendData;
